'use client';

import { io } from 'socket.io-client';

import { AUTH_APPROVAL_ROTATE_GRACE_MS, AUTH_APPROVAL_SOCKET_EVENT, AUTH_APPROVAL_SOCKET_PATH } from '@/constants/auth/authApproval';
import { AuthApprovalStatus } from '@/enums/AuthApprovalStatus';
import { AuthApprovalWatchKind } from '@/enums/AuthApprovalWatchKind';
import type { AuthApprovalStatusChangedArgs } from '@/interfaces/AuthApprovalRealtime';

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === 'object' && !Array.isArray(value));

const authApprovalStatusSet = new Set<string>(Object.values(AuthApprovalStatus));
const authApprovalWatchKindSet = new Set<string>(Object.values(AuthApprovalWatchKind));
const authApprovalSocketUrl = process.env.NEXT_PUBLIC_CHATTENGER_AUTH_APPROVAL_SOCKET_URL?.trim();

export const getAuthApprovalRotateDelayMs = ({ expiresAt, nowMs = Date.now() }: { expiresAt: string; nowMs?: number }) =>
    Math.max(0, new Date(expiresAt).getTime() - nowMs - AUTH_APPROVAL_ROTATE_GRACE_MS);

export const isAuthApprovalStatusChangedArgs = (value: unknown): value is AuthApprovalStatusChangedArgs => {
    if (!isRecord(value)) {
        return false;
    }

    return (
        authApprovalWatchKindSet.has(String(value.kind)) &&
        authApprovalStatusSet.has(String(value.status)) &&
        typeof value.token === 'string' &&
        (typeof value.expiresAt === 'string' || value.expiresAt === null)
    );
};

export const watchAuthApprovalStatus = ({
    deviceId,
    kind,
    onStatus,
    token,
}: {
    deviceId: string;
    kind: AuthApprovalWatchKind;
    onStatus: (payload: AuthApprovalStatusChangedArgs) => void;
    token: string;
}) => {
    const socketOptions = {
        auth: {
            authWatch: {
                deviceId,
                kind,
                token,
            },
        },
        path: AUTH_APPROVAL_SOCKET_PATH,
        reconnection: true,
        reconnectionDelay: 500,
        reconnectionDelayMax: 1_500,
        timeout: 8_000,
        transports: ['websocket', 'polling'],
        withCredentials: true,
    };
    const socket = authApprovalSocketUrl ? io(authApprovalSocketUrl, socketOptions) : io(socketOptions);

    socket.on(AUTH_APPROVAL_SOCKET_EVENT, (payload: unknown) => {
        if (isAuthApprovalStatusChangedArgs(payload)) {
            onStatus(payload);
        }
    });

    return () => {
        socket.disconnect();
    };
};
